const OUTPUT_MIME_TYPE = 'image/jpeg';

// Formats the media service rejects but mainstream browsers can decode +
// re-encode to JPEG via canvas reliably (Safari, Chrome >=85, Firefox >=93).
export const FORMATS_REQUIRING_CONVERSION: ReadonlySet<string> = new Set([
  'image/heic',
  'image/heif',
  'image/avif',
]);

export const shouldReencodeForUpload = ({
  fileType,
  fileSize,
  maxFileSizeBytes,
  isWithinDimensions,
}: {
  fileType: string;
  fileSize: number;
  maxFileSizeBytes: number;
  isWithinDimensions: boolean;
}): boolean =>
  FORMATS_REQUIRING_CONVERSION.has(fileType) || fileSize > maxFileSizeBytes || !isWithinDimensions;

export const resizeAndCompressImage = async ({
  file,
  maxWidth,
  maxHeight,
  quality = 0.7,
}: {
  file: File;
  maxWidth: number;
  maxHeight: number;
  quality?: number;
}): Promise<File> => {
  const { image, width, height } = await createImage(file);

  const { width: newWidth, height: newHeight } = calculateResize({
    maxWidth,
    maxHeight,
    width,
    height,
  });

  const resizedBlob = await resizeImage({
    image,
    type: OUTPUT_MIME_TYPE,
    width: newWidth,
    height: newHeight,
    quality,
  });

  return new File([resizedBlob], replaceExtension(file.name, 'jpg'), {
    type: OUTPUT_MIME_TYPE,
    lastModified: Date.now(),
  });
};

const replaceExtension = (fileName: string, newExtension: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  const base = lastDot === -1 ? fileName : fileName.slice(0, lastDot);
  return `${base}.${newExtension}`;
};

const resizeImage = async ({
  image,
  type,
  width,
  height,
  quality,
}: {
  image: HTMLImageElement;
  type: string;
  width: number;
  height: number;
  quality: number;
}): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return reject('Could not get 2d context');
    }
    ctx.drawImage(image, 0, 0, width, height);
    canvas.toBlob(
      (blob) => (!blob ? reject('Could not create blob') : resolve(blob)),
      type,
      quality,
    );
  });

export const calculateResize = ({
  maxWidth,
  maxHeight,
  width,
  height,
}: {
  maxWidth: number;
  maxHeight: number;
  width: number;
  height: number;
}): { width: number; height: number } => {
  if (width > height) {
    if (width > maxWidth) {
      return {
        width: maxWidth,
        height: (height * maxWidth) / width,
      };
    }
  } else if (height > maxHeight) {
    return {
      width: (width * maxHeight) / height,
      height: maxHeight,
    };
  }

  return { width, height };
};

export const checkDimensions = async ({
  file,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
}: {
  file: File;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}): Promise<boolean> => {
  const { width, height } = await createImage(file);

  const isValid =
    (minWidth === undefined || width >= minWidth) &&
    (maxWidth === undefined || width <= maxWidth) &&
    (minHeight === undefined || height >= minHeight) &&
    (maxHeight === undefined || height <= maxHeight);

  return isValid;
};

export const createImage = async (
  file: File,
): Promise<{ image: HTMLImageElement; width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      const { width, height } = image;
      resolve({ image, width, height });
    };
    image.onerror = () => reject(new Error(`Could not decode image of type ${file.type}`));
  });
